import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Body1 } from "@material/react-typography";
import {
  Dialog,
  DialogContent,
  DialogButton,
  DialogTitle,
} from "~/components/dialog";
import { DialogFooter } from "~/components/dialog-footer";
import { useIsActive } from "../useIsActive";
import { getExpiration } from "../jwtUtils";
import {
  getGoogleUserManager,
  getAzureUserManager,
  OidcProvider,
} from "../oidcUserManagers";

type RenewalState = "ok" | "inactive-prompt" | "renewing" | "reauth-prompt";

type TokenRenewalManagerProps = {
  jwt: string;
  provider: OidcProvider;
  logout: () => void;
};

const NEAR_EXPIRE_THRESHOLD_SECS = 600;

export const TokenRenewalManager: React.FC<TokenRenewalManagerProps> = ({
  jwt,
  provider,
  logout,
}) => {
  const isActive = useIsActive();
  const [searchParams] = useSearchParams();
  const nearExpireThreshold = parseInt(
    searchParams.get("nearExpireThreshold") ||
      String(NEAR_EXPIRE_THRESHOLD_SECS)
  );
  const [state, setState] = useState<RenewalState>("ok");
  const attemptedRef = useRef(false);

  const getManager = useCallback(
    () =>
      provider === "google" ? getGoogleUserManager() : getAzureUserManager(),
    [provider]
  );

  const doSilentRenew = useCallback(async () => {
    setState("renewing");
    attemptedRef.current = true;
    try {
      await getManager().signinSilent();
      setState("ok");
      attemptedRef.current = false;
    } catch {
      setState("reauth-prompt");
    }
  }, [getManager]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (state !== "ok" || attemptedRef.current) {
        return;
      }

      const expireTime = getExpiration(jwt).getTime();
      const nowTime = Date.now();
      const expiresInSecs = (expireTime - nowTime) / 1000;
      const nearExpire = expiresInSecs < nearExpireThreshold;

      if (!nearExpire) {
        return;
      }

      if (isActive) {
        void doSilentRenew();
      } else {
        setState("inactive-prompt");
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [jwt, isActive, state, nearExpireThreshold, doSilentRenew]);

  const handleContinue = () => {
    void doSilentRenew();
  };

  const handleReauth = () => {
    void getManager()
      .signinPopup()
      .then(() => {
        setState("ok");
        attemptedRef.current = false;
      })
      .catch(() => {
        setState("reauth-prompt");
      });
  };

  if (state === "inactive-prompt") {
    return (
      <Dialog open onClose={() => undefined} className="reauth-prompt">
        <DialogTitle>Login Info Expiring</DialogTitle>
        <DialogContent>
          <Body1>
            Your login session is about to expire. Please log out if you are
            finished working otherwise, click &apos;Continue Working&apos;.
            Thanks for helping keep our systems secure!
          </Body1>
        </DialogContent>
        <DialogFooter>
          <DialogButton action="cancel" onClick={logout}>
            Log Out
          </DialogButton>
          <DialogButton
            action="confirm"
            isDefaultAction
            onClick={handleContinue}
          >
            Continue Working
          </DialogButton>
        </DialogFooter>
      </Dialog>
    );
  }

  if (state === "reauth-prompt") {
    return (
      <Dialog open onClose={() => undefined} className="reauth-prompt">
        <DialogTitle>Login Info Expiring</DialogTitle>
        <DialogContent>
          <Body1>
            We&apos;re having trouble automatically renewing your authentication
            information. Click &apos;Re-authenticate&apos; below to
            authenticate.
          </Body1>
        </DialogContent>
        <DialogFooter>
          <DialogButton action="cancel" onClick={logout}>
            Log Out
          </DialogButton>
          <DialogButton action="confirm" isDefaultAction onClick={handleReauth}>
            Re-authenticate
          </DialogButton>
        </DialogFooter>
      </Dialog>
    );
  }

  return null;
};
